/**
 * Cards are small integers 0..51 so they are cheap on the wire and trivially
 * comparable. id = suit * 13 + (rank - 1). Rank 1 is the ace, 11/12/13 are
 * jack/queen/king. The pegging/fifteen VALUE of a face card is 10; its RANK is
 * still 11..13, which is what runs and pairs use.
 */

export type Card = number;
export type Suit = 0 | 1 | 2 | 3; // ♠ ♥ ♦ ♣

export const SUITS: readonly Suit[] = [0, 1, 2, 3];
export const SUIT_SYMBOL = ['♠', '♥', '♦', '♣'] as const;
export const SUIT_NAME = ['spades', 'hearts', 'diamonds', 'clubs'] as const;
export const RANK_LABEL = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'] as const;
export const RANK_NAME = [
  '',
  'ace',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'jack',
  'queen',
  'king',
] as const;

export function rankOf(c: Card): number {
  return (c % 13) + 1;
}
export function suitOf(c: Card): Suit {
  return Math.floor(c / 13) as Suit;
}
/** Counting value: ace 1, pips face value, J/Q/K 10. */
export function valueOf(c: Card): number {
  return Math.min(rankOf(c), 10);
}
export function makeCard(rank: number, suit: Suit): Card {
  return suit * 13 + (rank - 1);
}
export function isRed(c: Card): boolean {
  const s = suitOf(c);
  return s === 1 || s === 2;
}
export function label(c: Card): string {
  return `${RANK_LABEL[rankOf(c)]}${SUIT_SYMBOL[suitOf(c)]}`;
}
export function longName(c: Card): string {
  return `${RANK_NAME[rankOf(c)]} of ${SUIT_NAME[suitOf(c)]}`;
}
export function isCard(x: unknown): x is Card {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 51;
}

/** Parse "5H", "10S", "JD", "AC" — used by tests and the coach text. */
export function parseCard(s: string): Card {
  const m = /^(10|[A2-9JQK])([SHDC♠♥♦♣])$/i.exec(s.trim());
  if (!m) throw new Error(`bad card ${s}`);
  const r = m[1]!.toUpperCase();
  const rank = r === 'A' ? 1 : r === 'J' ? 11 : r === 'Q' ? 12 : r === 'K' ? 13 : Number(r);
  const su = m[2]!.toUpperCase();
  const suit = ('SHDC'.indexOf(su) >= 0 ? 'SHDC'.indexOf(su) : '♠♥♦♣'.indexOf(su)) as Suit;
  return makeCard(rank, suit);
}
export function parseCards(s: string): Card[] {
  return s.split(/[\s,]+/).filter(Boolean).map(parseCard);
}

export function fullDeck(): Card[] {
  return Array.from({ length: 52 }, (_, i) => i);
}

/** Sort for display: by rank, then suit. */
export function sortCards(cards: readonly Card[]): Card[] {
  return [...cards].sort((a, b) => rankOf(a) - rankOf(b) || suitOf(a) - suitOf(b));
}

export function sum(cards: readonly Card[]): number {
  let t = 0;
  for (const c of cards) t += valueOf(c);
  return t;
}

/**
 * A small seedable PRNG (mulberry32). The engine never calls Math.random; a
 * deck is shuffled with a seed so a game — and a bug report — can be replayed.
 */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffle(deck: Card[], random: () => number): Card[] {
  const d = [...deck];
  for (let i = d.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [d[i], d[j]] = [d[j]!, d[i]!];
  }
  return d;
}

/** A fresh unpredictable seed. Uses crypto where available. */
export function randomSeed(): number {
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array } };
  if (g.crypto?.getRandomValues) {
    const a = new Uint32Array(1);
    g.crypto.getRandomValues(a);
    return a[0]!;
  }
  return Math.floor(Math.random() * 0xffffffff);
}

export function combinations<T>(items: readonly T[], k: number): T[][] {
  const out: T[][] = [];
  const n = items.length;
  const idx: number[] = [];
  const rec = (start: number) => {
    if (idx.length === k) {
      out.push(idx.map((i) => items[i]!));
      return;
    }
    for (let i = start; i < n; i++) {
      idx.push(i);
      rec(i + 1);
      idx.pop();
    }
  };
  rec(0);
  return out;
}
